import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Square, Filter, RotateCcw, Save, RefreshCw, ShieldCheck, ChevronDown, AlertTriangle } from "lucide-react";
import { useRefresh } from "@/context/RefreshContext";
import { useLang } from "@/context/LanguageContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export const SYSTEM_PROFILES = {
  standard_hydraulics: {
    label: "Conventionele industriële hydrauliek",
    labelEn: "Conventional industrial hydraulics",
    description: "Voor tandwielpompen, cilinders en standaard 4/2- en 4/3-richtingsventielen.",
    descriptionEn: "For gear pumps, cylinders and standard 4/2 and 4/3 directional valves.",
    start: "19/17/14",
  },
  piston_pump_system: {
    label: "Plunjerpomp- en hydrostatische systemen",
    labelEn: "Piston pump and hydrostatic systems",
    description: "Voor axiale en radiale plunjerpompen, regelpompen en hydrostatische aandrijvingen.",
    descriptionEn: "For axial and radial piston pumps, variable pumps and hydrostatic drives.",
    start: "18/16/13",
  },
  proportional_hydraulics: {
    label: "Proportioneel geregelde hydrauliek",
    labelEn: "Proportionally controlled hydraulics",
    description: "Voor proportionele richtings-, druk- en debietventielen, inclusief OBE en LVDT.",
    descriptionEn: "For proportional directional, pressure and flow valves, including OBE and LVDT.",
    start: "17/15/12",
  },
};

const ASSESSMENT_HELP = {
  average: "Gebruikt het gemiddelde van alle geldige samples voor de ISO-beoordeling.",
  median: "Gebruikt de middelste geldige waarde en is daardoor minder gevoelig voor uitschieters.",
  worst_valid: "Gebruikt per ISO-kanaal de hoogste geldige sample en beoordeelt de olie daarmee het strengst.",
};

const INSUFFICIENT_DATA_HELP = {
  retry: "De RevPi probeert de volledige meting opnieuw uit te voeren.",
  skip: "De meetcyclus eindigt zonder een nieuwe reinheidsbeoordeling.",
  fault: "De RevPi meldt een storing en stopt de normale meetcyclus.",
};

const DEFAULT_SETTINGS = {
  schema_version: 2,
  system_profile: "standard_hydraulics",
  schedule: {
    mode: "interval",
    cycle_interval_seconds: 600,
    fixed_times: [],
    timezone: "Europe/Amsterdam",
    weekdays: [1, 2, 3, 4, 5, 6, 7],
    warmup_seconds: 90,
    samples_per_cycle: 5,
    seconds_between_samples: 20,
    minimum_valid_samples: 3,
    evaluate_after_measurement: true,
  },
  filter_control: {
    automatic_enabled: false,
    control_interval_seconds: 600,
    filter_max_seconds: 3600,
    settle_seconds: 120,
    control_measurements: 2,
    seconds_between_control_measurements: 20,
    assessment_method: "average",
  },
  sensors: { cs1000_enabled: true, hlb1400_enabled: true },
  fault_behavior: { sensor_retry_count: 3, sensor_retry_delay_seconds: 10, insufficient_valid_samples: "retry" },
  alarms: {
    water_saturation_pct: { warning: 60, critical: 80 },
    temperature_c: { warning: 60, critical: 75 },
    conductivity_ns_m: { warning: null, critical: null },
  },
};

const inMinutes = (seconds) => Number((Number(seconds || 0) / 60).toFixed(2));
const inSeconds = (minutes) => Math.round(Number(minutes || 0) * 60);

function settingsToForm(value = DEFAULT_SETTINGS) {
  const schedule = value.schedule || {};
  const filtering = value.filter_control || {};
  const fault = value.fault_behavior || {};
  const cycleSeconds = schedule.cycle_interval_seconds ?? Number(schedule.interval_minutes || 10) * 60;
  return {
    system_profile: value.system_profile || "standard_hydraulics",
    automatic_enabled: !!filtering.automatic_enabled,
    sample_frequency_minutes: inMinutes(cycleSeconds),
    warmup_minutes: inMinutes(schedule.warmup_seconds ?? 90),
    samples_per_cycle: Number(schedule.samples_per_cycle ?? 5),
    sample_interval_seconds: Number(schedule.seconds_between_samples ?? 20),
    minimum_valid_samples: Number(schedule.minimum_valid_samples ?? 3),
    control_interval_minutes: inMinutes(filtering.control_interval_seconds ?? filtering.normal_duration_seconds ?? 600),
    filter_max_minutes: inMinutes(filtering.filter_max_seconds ?? filtering.max_continuous_runtime_seconds ?? 3600),
    stabilization_minutes: inMinutes(filtering.settle_seconds ?? 120),
    control_measurements: Number(filtering.control_measurements ?? 2),
    control_sample_interval_seconds: Number(filtering.seconds_between_control_measurements ?? 20),
    assessment_method: filtering.assessment_method === "maximum" ? "worst_valid" : filtering.assessment_method || "average",
    sensor_retry_count: Number(fault.sensor_retry_count ?? 3),
    sensor_retry_delay_seconds: Number(fault.sensor_retry_delay_seconds ?? 10),
    insufficient_valid_samples: fault.insufficient_valid_samples || "retry",
  };
}

function formToSettings(form, source) {
  const base = source || DEFAULT_SETTINGS;
  return {
    ...base,
    schema_version: 2,
    system_profile: form.system_profile,
    schedule: {
      ...(base.schedule || DEFAULT_SETTINGS.schedule),
      mode: "interval",
      cycle_interval_seconds: inSeconds(form.sample_frequency_minutes),
      warmup_seconds: inSeconds(form.warmup_minutes),
      samples_per_cycle: Number(form.samples_per_cycle),
      seconds_between_samples: Number(form.sample_interval_seconds),
      minimum_valid_samples: Number(form.minimum_valid_samples),
      evaluate_after_measurement: true,
    },
    filter_control: {
      automatic_enabled: !!form.automatic_enabled,
      control_interval_seconds: inSeconds(form.control_interval_minutes),
      filter_max_seconds: inSeconds(form.filter_max_minutes),
      settle_seconds: inSeconds(form.stabilization_minutes),
      control_measurements: Number(form.control_measurements),
      seconds_between_control_measurements: Number(form.control_sample_interval_seconds),
      assessment_method: form.assessment_method,
    },
    sensors: base.sensors || DEFAULT_SETTINGS.sensors,
    fault_behavior: {
      ...(base.fault_behavior || DEFAULT_SETTINGS.fault_behavior),
      sensor_retry_count: Number(form.sensor_retry_count),
      sensor_retry_delay_seconds: Number(form.sensor_retry_delay_seconds),
      insufficient_valid_samples: form.insufficient_valid_samples,
    },
    alarms: base.alarms || DEFAULT_SETTINGS.alarms,
  };
}

function NumberField({ label, value, onChange, unit, min = 0, max, step = 1, help }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input type="number" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} className="pr-12 font-mono" />
        {unit && <span className="pointer-events-none absolute right-3 top-2 text-xs text-muted-foreground">{unit}</span>}
      </div>
      {help && <p className="text-[11px] leading-4 text-muted-foreground">{help}</p>}
    </div>
  );
}

export function DeviceControl({ deviceId, overview, onRefresh }) {
  const { tick } = useRefresh();
  const { t, lang } = useLang();
  const [settings, setSettings] = useState(settingsToForm(DEFAULT_SETTINGS));
  const [savedSettings, setSavedSettings] = useState(settingsToForm(DEFAULT_SETTINGS));
  const [sourceSettings, setSourceSettings] = useState(DEFAULT_SETTINGS);
  const [versions, setVersions] = useState([]);
  const [commands, setCommands] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async ({ preserveForm = false } = {}) => {
    const [configurationResponse, commandResponse] = await Promise.all([
      api.get(`/devices/${deviceId}/configurations`),
      api.get(`/devices/${deviceId}/commands`),
    ]);
    const configurationVersions = configurationResponse.data || [];
    setVersions(configurationVersions);
    setCommands(commandResponse.data || []);
    if (!preserveForm) {
      const latest = configurationVersions[0]?.settings || DEFAULT_SETTINGS;
      const formSettings = settingsToForm(latest);
      setSourceSettings(latest);
      setSettings(formSettings);
      setSavedSettings(formSettings);
    }
  };

  useEffect(() => { load().catch(() => {}); }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tick) load({ preserveForm: true }).catch(() => {}); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const profile = SYSTEM_PROFILES[settings.system_profile] || SYSTEM_PROFILES.standard_hydraulics;
  const pendingCount = commands.filter((command) => ["pending_sync", "delivered", "accepted", "running"].includes(command.status)).length;
  const payload = useMemo(() => formToSettings(settings, sourceSettings), [settings, sourceSettings]);
  const hasUnsavedChanges = useMemo(() => JSON.stringify(settings) !== JSON.stringify(savedSettings), [settings, savedSettings]);
  const update = (key, value) => setSettings((current) => ({ ...current, [key]: value }));

  const run = async (action, parameters = {}) => {
    setBusy(true);
    try {
      await api.post(`/devices/${deviceId}/commands`, { action, parameters });
      toast.success(t("Opdracht staat klaar en wordt bij de volgende verbinding naar de RevPi gestuurd.", "The command is queued and will be sent to the RevPi at its next connection."));
      await load({ preserveForm: true });
      onRefresh?.();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (settings.minimum_valid_samples > settings.samples_per_cycle) {
      toast.error(t("Benodigde geldige samples mogen niet hoger zijn dan het totale aantal samples.", "Required valid samples cannot exceed the total sample count."));
      return;
    }
    if (payload.filter_control.control_interval_seconds > payload.filter_control.filter_max_seconds) {
      toast.error(t("De maximale filtertijd moet minimaal gelijk zijn aan één filterinterval.", "Maximum filtering time must be at least one filtering interval."));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/devices/${deviceId}/configurations`, {
        base_config_version: overview?.active_config_version || 0,
        settings: payload,
      });
      toast.success(t("Instellingen opgeslagen. De RevPi neemt ze bij de volgende verbinding over.", "Settings saved. The RevPi will apply them at its next connection."));
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <section className="space-y-5 rounded-lg border border-border bg-card p-5" data-testid="revpi-control">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-bold">{t("Apparaat bedienen", "Device control")}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{t("Een opdracht wordt uitgevoerd zodra de RevPi weer synchroniseert.", "A command is executed as soon as the RevPi synchronizes again.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{t("Actief", "Active")} v{overview?.active_config_version ?? 0}</Badge>
          <Badge variant="secondary">{pendingCount === 0 ? t("Geen opdrachten in wachtrij", "No queued commands") : t(`${pendingCount} opdracht${pendingCount === 1 ? "" : "en"} wacht op de RevPi`, `${pendingCount} command${pendingCount === 1 ? "" : "s"} waiting for the RevPi`)}</Badge>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" aria-label={t("Directe bediening", "Direct controls")}>
        <Button size="sm" variant="destructive" onClick={() => run("STOP")} disabled={busy}><Square className="mr-1.5 h-4 w-4" />{t("Lopende actie stoppen", "Stop current action")}</Button>
        <Button size="sm" variant="outline" onClick={() => run("START_FILTER", { max_duration_seconds: payload.filter_control.filter_max_seconds })} disabled={busy}><Filter className="mr-1.5 h-4 w-4" />{t("Filtercyclus starten", "Start filtering cycle")}</Button>
        <AlertDialog><AlertDialogTrigger asChild><Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700" disabled={busy} data-testid="reset-device-button"><RotateCcw className="mr-1.5 h-4 w-4" />{t("Reset", "Reset")}</Button></AlertDialogTrigger><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t("THRM-service veilig resetten?", "Safely reset the THRM service?")}</AlertDialogTitle><AlertDialogDescription>{t("Alle uitgangen worden eerst veilig uitgeschakeld. Daarna wordt alleen de THRM-service opnieuw gestart. Meetdata, instellingen en configuratieversies blijven behouden.", "All outputs are switched off safely first. Then only the THRM service is restarted. Measurements, settings and configuration versions are preserved.")}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>{t("Annuleren", "Cancel")}</AlertDialogCancel><AlertDialogAction className="bg-blue-600 hover:bg-blue-700" onClick={() => run("RESTART_SERVICE")} data-testid="confirm-reset-device">{t("Reset uitvoeren", "Run reset")}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
        <Button size="sm" variant="ghost" onClick={() => load({ preserveForm: true })} disabled={busy}><RefreshCw className="mr-1.5 h-4 w-4" />{t("Vernieuwen", "Refresh")}</Button>
      </div>

      <details className="group rounded-lg border border-border" data-testid="device-settings">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3">
          <div>
            <div className="text-sm font-bold">{t("Meet- en filterinstellingen", "Measurement and filtering settings")}</div>
            <div className="mt-0.5 text-[11px] font-normal text-muted-foreground">{t("Reinheidsprofiel, samplefrequentie, controlemetingen en maximale filtertijd", "Cleanliness profile, sample frequency, control measurements and maximum filtering time")}</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {hasUnsavedChanges && <Badge className="border-0 bg-amber-500/10 text-amber-600">{t("Wijzigingen nog niet opgeslagen", "Unsaved changes")}</Badge>}
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
          </div>
        </summary>
        <div className="space-y-5 border-t border-border p-4">
      {hasUnsavedChanges && (
        <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3.5 py-3 text-sm text-amber-700 dark:text-amber-400" role="status" data-testid="unsaved-settings-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div><span className="font-bold">{t("Wijzigingen nog niet opgeslagen.", "Changes have not been saved.")}</span> {t("Klik onderaan op", "Click")} <span className="font-semibold">{t("Instellingen als nieuwe versie opslaan", "Save settings as a new version")}</span> {t("om ze naar de RevPi door te voeren.", "below to send them to the RevPi.")}</div>
        </div>
      )}
      <div className="grid gap-4 xl:grid-cols-[1.05fr_1.45fr]">
        <div className="space-y-4 rounded-lg border border-border bg-muted/20 p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-bold">{t("Reinheidsprofiel", "Cleanliness profile")}</h3>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("Type hydraulisch systeem", "Hydraulic system type")}</Label>
            <Select value={settings.system_profile} onValueChange={(value) => update("system_profile", value)}>
              <SelectTrigger data-testid="system-profile-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SYSTEM_PROFILES).map(([code, item]) => <SelectItem key={code} value={code}>{lang === "en" ? item.labelEn : item.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] leading-4 text-muted-foreground">{t("Bepaalt automatisch vanaf welke ISO-waarde een filtercyclus mag starten.", "Automatically determines the ISO value from which a filtering cycle may start.")}</p>
            <p className="text-[11px] leading-4 text-muted-foreground">{lang === "en" ? profile.descriptionEn : profile.description}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-card p-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t("Inschakelgrens filtering", "Filtering start limit")}</div>
              <div className="mt-1 font-mono text-lg font-semibold text-amber-600">ISO {profile.start}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t("Reinigingsdoel", "Cleaning target")}</div>
              <div className="mt-1 font-mono text-lg font-semibold text-emerald-600">ISO 17/15/12</div>
            </div>
          </div>
          <p className="text-[11px] leading-4 text-muted-foreground">{t("De RevPi beoordeelt de olie lokaal. Filtering start zodra minimaal één beoordeelde ISO-waarde de inschakelgrens overschrijdt en stopt zodra alle drie de waarden ISO 17/15/12 of beter zijn.", "The RevPi assesses the oil locally. Filtering starts when at least one assessed ISO value exceeds the start limit and stops when all three values are ISO 17/15/12 or better.")}</p>
          <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-card p-3">
            <div>
              <Label htmlFor="automatic-filtering" className="text-sm font-semibold">{t("Automatisch filteren", "Automatic filtering")}</Label>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{t("Laat de RevPi zelfstandig beoordelen en filteren zodra de profielgrens wordt overschreden.", "Allows the RevPi to assess and filter independently when the profile limit is exceeded.")}</p>
            </div>
            <Switch id="automatic-filtering" checked={settings.automatic_enabled} onCheckedChange={(value) => update("automatic_enabled", value)} data-testid="automatic-filtering-switch" />
          </div>
        </div>

        <div className="space-y-4 rounded-lg border border-border p-4">
          <div>
            <h3 className="text-sm font-bold">{t("Normale meetcyclus", "Normal measurement cycle")}</h3>
            <p className="mt-1 text-[11px] text-muted-foreground">{t(`Elke ${settings.sample_frequency_minutes || 0} minuten start een nieuwe meetcyclus.`, `A new measurement cycle starts every ${settings.sample_frequency_minutes || 0} minutes.`)}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <NumberField label={t("Samplefrequentie", "Sample frequency")} value={settings.sample_frequency_minutes} onChange={(value) => update("sample_frequency_minutes", value)} unit="min" min={1} max={1440} help={t("Tijd tussen de start van twee normale meetcycli.", "Time between the start of two normal measurement cycles.")} />
            <NumberField label={t("Opwarmtijd sensoren", "Sensor warm-up time")} value={settings.warmup_minutes} onChange={(value) => update("warmup_minutes", value)} unit="min" min={0} max={60} step={0.5} help={t("Tijd dat de sensoren opwarmen voordat de eerste sample wordt genomen.", "Time for the sensors to warm up before the first sample is taken.")} />
            <NumberField label={t("Samples per meting", "Samples per measurement")} value={settings.samples_per_cycle} onChange={(value) => update("samples_per_cycle", value)} unit="x" min={1} max={50} help={t("Aantal meetwaarden dat tijdens één normale meetcyclus wordt verzameld.", "Number of values collected during one normal measurement cycle.")} />
            <NumberField label={t("Tijd tussen samples", "Time between samples")} value={settings.sample_interval_seconds} onChange={(value) => update("sample_interval_seconds", value)} unit="sec" min={0} max={3600} help={t("Wachttijd tussen twee samples binnen dezelfde meetcyclus.", "Wait time between two samples in the same measurement cycle.")} />
            <NumberField label={t("Benodigde geldige samples", "Required valid samples")} value={settings.minimum_valid_samples} onChange={(value) => update("minimum_valid_samples", value)} unit="x" min={1} max={50} help={t("Minimaal aantal bruikbare samples om de meetcyclus te mogen beoordelen.", "Minimum number of usable samples required to assess the measurement cycle.")} />
          </div>
        </div>
      </div>

      <details className="group rounded-lg border border-border" data-testid="advanced-filter-settings">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-bold">
          {t("Automatische reinigingscyclus & foutafhandeling", "Automatic cleaning cycle & fault handling")}
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-5 border-t border-border p-4">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("Automatische reinigingscyclus", "Automatic cleaning cycle")}</h4>
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <NumberField label={t("Filterinterval", "Filtering interval")} value={settings.control_interval_minutes} onChange={(value) => update("control_interval_minutes", value)} unit="min" min={1} max={1440} help={t("Werkelijke filterpomptijd tussen twee controlemetingen. Alleen deze filterblokken tellen mee voor de maximumtijd.", "Actual filtering pump time between two control measurements. Only these filtering blocks count toward the maximum time.")} />
              <NumberField label={t("Maximale filtertijd", "Maximum filtering time")} value={settings.filter_max_minutes} onChange={(value) => update("filter_max_minutes", value)} unit="min" min={1} max={1440} help={t("Maximale opgetelde filterpomptijd binnen één automatische reinigingscyclus. Stabilisatie en meten tellen niet mee.", "Maximum accumulated filtering pump time in one automatic cleaning cycle. Stabilization and measurement do not count.")} />
              <NumberField label={t("Stabilisatietijd vóór controle", "Stabilization time before control")} value={settings.stabilization_minutes} onChange={(value) => update("stabilization_minutes", value)} unit="min" min={0} max={60} step={0.5} help={t("Wachttijd na een filterblok zodat de olie tot rust komt voordat de RevPi opnieuw meet.", "Wait time after a filtering block so the oil can settle before the RevPi measures again.")} />
              <NumberField label={t("Samples per controlemeting", "Samples per control measurement")} value={settings.control_measurements} onChange={(value) => update("control_measurements", value)} unit="x" min={1} max={50} help={t("Aantal samples waarmee de RevPi één nieuw controlepunt en één ISO-beoordeling maakt.", "Number of samples used by the RevPi for one new control point and ISO assessment.")} />
              <NumberField label={t("Tijd tussen controlesamples", "Time between control samples")} value={settings.control_sample_interval_seconds} onChange={(value) => update("control_sample_interval_seconds", value)} unit="sec" min={0} max={3600} help={t("Wachttijd tussen de afzonderlijke samples binnen dezelfde controlemeting.", "Wait time between individual samples in the same control measurement.")} />
              <div className="space-y-1.5">
                <Label className="text-xs">{t("Beoordelingsmethode", "Assessment method")}</Label>
                <Select value={settings.assessment_method} onValueChange={(value) => update("assessment_method", value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="average">{t("Gemiddelde", "Average")}</SelectItem>
                    <SelectItem value="median">{t("Mediaan", "Median")}</SelectItem>
                    <SelectItem value="worst_valid">{t("Ongunstigste geldige waarde", "Worst valid value")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] leading-4 text-muted-foreground">{lang === "en" ? ({ average: "Uses the average of all valid samples for the ISO assessment.", median: "Uses the middle valid value and is less sensitive to outliers.", worst_valid: "Uses the highest valid sample per ISO channel for the strictest assessment." })[settings.assessment_method] : ASSESSMENT_HELP[settings.assessment_method]}</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("Sensorherhaling & onvoldoende meetdata", "Sensor retries & insufficient data")}</h4>
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
              <NumberField label={t("Sensorherhalingen", "Sensor retries")} value={settings.sensor_retry_count} onChange={(value) => update("sensor_retry_count", value)} unit="x" min={0} max={20} help={t("Aantal extra pogingen wanneer een sensor geen geldige waarde geeft.", "Number of extra attempts when a sensor does not return a valid value.")} />
              <NumberField label={t("Tijd tussen herhalingen", "Time between retries")} value={settings.sensor_retry_delay_seconds} onChange={(value) => update("sensor_retry_delay_seconds", value)} unit="sec" min={0} max={3600} help={t("Wachttijd voordat de sensor opnieuw wordt uitgelezen.", "Wait time before reading the sensor again.")} />
              <div className="space-y-1.5">
                <Label className="text-xs">{t("Bij onvoldoende geldige samples", "When valid samples are insufficient")}</Label>
                <Select value={settings.insufficient_valid_samples} onValueChange={(value) => update("insufficient_valid_samples", value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retry">{t("Meting opnieuw proberen", "Retry measurement")}</SelectItem>
                    <SelectItem value="skip">{t("Cyclus overslaan", "Skip cycle")}</SelectItem>
                    <SelectItem value="fault">{t("Storing melden", "Report fault")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] leading-4 text-muted-foreground">{lang === "en" ? ({ retry: "The RevPi retries the complete measurement.", skip: "The cycle ends without a new cleanliness assessment.", fault: "The RevPi reports a fault and stops the normal cycle." })[settings.insufficient_valid_samples] : INSUFFICIENT_DATA_HELP[settings.insufficient_valid_samples]}</p>
              </div>
            </div>
          </div>
        </div>
      </details>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>{t("Laatste configuratie", "Latest configuration")}: {versions[0] ? `v${versions[0].config_version} · ${versions[0].status}` : t("nog niet verzonden", "not sent yet")}</p>
          <p>{t("Laatste opdracht", "Latest command")}: {commands[0]
            ? `${commands[0].command_type || commands[0].action} · ${(commands[0].status_history || []).map((event) => event.status).join(" → ") || commands[0].status}`
            : t("nog geen opdracht", "no command yet")}</p>
        </div>
        <Button size="sm" onClick={save} disabled={busy || !hasUnsavedChanges} data-testid="save-device-settings"><Save className="mr-1.5 h-4 w-4" />{t("Instellingen als nieuwe versie opslaan", "Save settings as a new version")}</Button>
      </div>
        </div>
      </details>
    </section>
  );
}
